import { ReactNode } from 'react';

import { Box, styled } from '@mui/material';
import clsx from 'clsx';
import Prism, { highlight, languages } from 'prismjs';
import EditorCjs from 'react-simple-code-editor';
// Rolldown CJS-to-ESM interop may resolve to the exports object; unwrap .default if needed
const Editor = ((EditorCjs as any).default ?? EditorCjs) as typeof EditorCjs;

import { SxProps } from '../../types';
import { DsFormHelperText } from '../controls/form-helper-text';
import { DsFormLabel, FormLabelProps } from '../controls/form-label';
import './code-editor.css';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-sql';

import { tokens } from '../../theme/generated/tokens';

Prism.languages.cel = {
  comment: /\/\/.*/,
  function: {
    pattern: /[_a-zA-Z][_a-zA-Z0-9]*(?=\()/,
  },
  object: {
    pattern: /[_a-zA-Z][_a-zA-Z0-9]*(?=\{)/,
  },
  'attribute-name': /[_a-zA-Z][_a-zA-Z0-9]*:/,
  'object-member': /\.[_a-zA-Z][_a-zA-Z0-9]*/,
  'multiline-string': {
    pattern: /("""|''')[\s\S]*?\1/,
    alias: 'string',
  },
  string: {
    pattern: /(["'])(?:\\.|(?!\1)[^\\\r\n])*\1/,
    greedy: true,
  },
  'raw-string': {
    pattern: /([rR])(["'])(?:\\.|(?!\2)[^\\\r\n])*\2/,
    greedy: true,
  },
  'escape-sequence': {
    pattern:
      /\\[bfnrt"'\\]|\\u[0-9a-fA-F]{4}|\\U[0-9a-fA-F]{8}|\\[xX][0-9a-fA-F]{2}|\\0[0-8]{3}/,
  },
  number: /\b\d+\.?\d*\b/,
  'language-constants': /\b(true|false|null)\b/,
  'logical-operator': /&&|\|\|/,
  'comparison-operator': /<=|<|>=|>|==|!=|\bin\b/,
  'arithmetic-operator': /\+|-|\*|\/|%|!/,
  punctuation: /[{}()[\],:]/,
};

const highlightJson = (code: string) => {
  return highlight(code, languages.js, 'js');
};
const highlightBash = (code: string) => highlight(code, languages.bash, 'bash');
const highlightYaml = (code: string) => highlight(code, languages.yaml, 'yaml');
const highlightCel = (code: string) => highlight(code, languages.cel, 'cel');
const highlightSql = (code: string) => highlight(code, languages.sql, 'sql');

const highlightFnMap: Record<
  'json' | 'bash' | 'yaml' | 'cel' | 'sql',
  (c: string) => string | ReactNode
> = {
  bash: highlightBash,
  json: highlightJson,
  yaml: highlightYaml,
  cel: highlightCel,
  sql: highlightSql,
};

const StyledEditor = styled(Editor)({
  fontSize: 12,
  borderRadius: tokens.theme.radius.sm,
});

type Padding<T> = T | { top?: T; right?: T; bottom?: T; left?: T };

export type CodeEditorProps = {
  className?: string;
  hint?: ReactNode;
  error?: ReactNode;
  label?: ReactNode;
  highlight: keyof typeof highlightFnMap;
  onChange?: (value: string) => void;
  sx?: SxProps;
  rows?: number;

  // Props for the component
  value: string;
  tabSize?: number;
  insertSpaces?: boolean;
  ignoreTabKey?: boolean;
  padding?: Padding<number | string>;
  style?: React.CSSProperties;

  textareaId?: string;
  textareaClassName?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  form?: string;
  maxLength?: number;
  minLength?: number;
  name?: string;
  placeholder?: string;
  readOnly?: boolean;
  required?: boolean;
  onClick?: React.MouseEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;

  // Props for the highlighted code's pre element
  preClassName?: string;
} & FormLabelProps;

export const CodeEditor = ({
  className,
  onChange,
  hint,
  error,
  highlight,
  label,
  labelTooltipProps,
  sx,
  testId,
  ...rest
}: CodeEditorProps) => {
  return (
    <Box
      className={clsx('DS-CodeEditor-root', className)}
      sx={sx}
      data-testid={testId}
    >
      {!!label && (
        <DsFormLabel
          className="DS-CodeEditorLabel-root"
          labelTooltipProps={labelTooltipProps}
        >
          {label}
        </DsFormLabel>
      )}
      <StyledEditor
        onValueChange={(val) => {
          onChange?.(val);
        }}
        highlight={highlightFnMap[highlight]}
        className={clsx(`code-editor-${highlight} CodeEditor-editor`, {
          error: !!error,
        })}
        {
          ...(rest as any) /* cast required: react-simple-code-editor has incomplete prop types */
        }
      />
      {!!(error || hint) && (
        <DsFormHelperText className="DS-CodeEditor-hint" error={!!error}>
          {error || hint}
        </DsFormHelperText>
      )}
    </Box>
  );
};
